/**
 * Settings ▸ Billing ▸ what we are willing to sell.
 *
 * One row per Stripe Price: a (product, plan) for a subscription, or a credit
 * count for a pack. THIS TABLE IS THE ALLOWLIST — Checkout refuses anything
 * without an active row here, so selling a second product is adding rows, not
 * changing code, and taking one off sale is one click.
 *
 * The dollar amount is never typed. It is read back from Stripe when the row
 * is saved, because a price that says $29 here and charges $49 there is a
 * ticket the customer opens after they have been charged.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { PRODUCT_INFO } from '../../shell/products';
import type { PlanInfo, PlanKey, Product, StripePriceRow } from '../../types';

function money(row: StripePriceRow): string {
  if (row.unit_amount_usd == null) return '-';
  const amount = row.unit_amount_usd % 1 === 0
    ? `$${row.unit_amount_usd.toFixed(0)}`
    : `$${row.unit_amount_usd.toFixed(2)}`;
  return row.interval ? `${amount}/${row.interval}` : amount;
}

function describe(row: StripePriceRow): string {
  if (row.kind === 'TOPUP') return `${(row.credits ?? 0).toLocaleString()} credits`;
  const product = row.product ? (PRODUCT_INFO[row.product]?.name ?? row.product) : '-';
  return `${product} · ${row.plan ?? '-'}`;
}

export default function StripePrices({ plans }: { plans: Record<string, PlanInfo> }) {
  const [rows, setRows] = useState<StripePriceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Only plans the server marks purchasable can carry a price — free is where
  // everyone starts and enterprise is a conversation. The backend refuses the
  // others too; this just keeps them out of the picker.
  const buyable = Object.values(plans).filter((p) => p.purchasable);

  const [kind, setKind] = useState<'SUBSCRIPTION' | 'TOPUP'>('SUBSCRIPTION');
  const [priceId, setPriceId] = useState('');
  const [product, setProduct] = useState<Product>('BOARDROOM');
  const [plan, setPlan] = useState<PlanKey>('starter');
  const [credits, setCredits] = useState('');
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await api.adminGetStripePrices());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load prices');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.adminPutStripePrice({
        kind,
        stripe_price_id: priceId.trim(),
        product: kind === 'SUBSCRIPTION' ? product : null,
        plan: kind === 'SUBSCRIPTION' ? plan : null,
        credits: kind === 'TOPUP' ? Number(credits) || null : null,
        label: label.trim() || null,
      });
      setPriceId('');
      setCredits('');
      setLabel('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Stripe would not accept that price');
    } finally {
      setBusy(false);
    }
  };

  const retire = async (row: StripePriceRow) => {
    if (!window.confirm(`Take ${describe(row)} off sale? Existing subscriptions keep renewing.`)) return;
    await api.adminRetireStripePrice(row.id);
    await load();
  };

  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">What we sell</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        One row per Stripe Price. A plan with no live row here has no buy button and Checkout refuses it,
        so this table is what decides what is on sale. The amount is read back from Stripe when you save -
        it is never typed, so what customers see is what they are charged.
      </p>

      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium">What</th>
              <th className="text-left px-3 py-2 font-medium">Price</th>
              <th className="text-left px-3 py-2 font-medium">Stripe price id</th>
              <th className="text-left px-3 py-2 font-medium">On sale</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows?.map((row) => (
              <tr key={row.id} data-testid={`price-${row.id}`} className={row.active ? '' : 'opacity-60'}>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                  {describe(row)}
                  {row.label && <span className="text-gray-400"> · {row.label}</span>}
                </td>
                <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{money(row)}</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                  {row.stripe_price_id}
                </td>
                <td className="px-3 py-2">{row.active ? 'Yes' : 'Retired'}</td>
                <td className="px-3 py-2 text-right">
                  {row.active && (
                    <button
                      type="button"
                      onClick={() => retire(row)}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Take off sale
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-gray-500 dark:text-gray-400">
                  Nothing is on sale yet, so nobody can buy anything. Add a price below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 dark:text-gray-400">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as 'SUBSCRIPTION' | 'TOPUP')}
            className="mt-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
          >
            <option value="SUBSCRIPTION">Plan</option>
            <option value="TOPUP">Credit pack</option>
          </select>
        </label>

        {kind === 'SUBSCRIPTION' ? (
          <>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 dark:text-gray-400">Product</span>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value as Product)}
                className="mt-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              >
                {(Object.keys(PRODUCT_INFO) as Product[]).map((p) => (
                  <option key={p} value={p}>{PRODUCT_INFO[p].name}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 dark:text-gray-400">Plan</span>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value as PlanKey)}
                className="mt-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
              >
                {buyable.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="text-sm">
            <span className="block text-xs text-gray-500 dark:text-gray-400">Credits</span>
            <input
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              inputMode="numeric"
              placeholder="1000"
              className="mt-0.5 w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
            />
          </label>
        )}

        <label className="text-sm flex-1 min-w-[16rem]">
          <span className="block text-xs text-gray-500 dark:text-gray-400">Stripe price id</span>
          <input
            value={priceId}
            onChange={(e) => setPriceId(e.target.value)}
            placeholder="price_1A2b3C…"
            className="mt-0.5 w-full font-mono rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 dark:text-gray-400">Label (optional)</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1"
          />
        </label>
        <button
          type="button"
          onClick={add}
          disabled={busy || !priceId.trim()}
          className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? 'Checking with Stripe…' : 'Add'}
        </button>
      </div>
    </section>
  );
}
